/*
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 *
 * Copyright: 2023 by Idemia Identity & Security USA LLC. All rights reserved.
 * License: In accordance  Idemia I&S USA LLC's license agreement.
 * Code Classification: GOVERNMENT
 *
 * Classification Person: Nadim Bakizada nadim.bakizada@us.idemia.com
 * Classification Reason: Software not specific to any U.S. Government Entity
 * Classification Date: 2023
 *
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 */

import { formatDate } from "utils/formatDate";


type PrintOptions = {
  printBackground?: boolean;
  title?: string;
};

const printTextOptionsDefault: PrintOptions = {
  printBackground: false,
  title: "CARES",
};

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const normalizeLineBreaks = (text: string) => text.replace(/\r\n/g, "\n");

export const formatPlainTextForEditor = (text: string, isHtml: boolean = false) => {
  if (isHtml) {
    return text.replace(/<\/?displayHtml>/g, '');
  }
  return normalizeLineBreaks(escapeHtml(text ?? "")).replace(/\n/g, "<br />");
};


export const formatEditorTextForPrint = (text: string) => `<pre class="break-line">${normalizeLineBreaks(escapeHtml(text ?? ""))}</pre>`;

const printText = async (
  textToPrint: string,
  authorName: string,
  noteDescription: string,
  printOptions?: PrintOptions,
  attachments?: string[]
) => {
  const { printBackground, title } = { ...printTextOptionsDefault, ...printOptions };

  const printFrame = document.createElement('iframe');
  printFrame.name = "printFrame";
  printFrame.style.position = "absolute";
  printFrame.style.top = "-1000000px";
  document.body.appendChild(printFrame);

  const frameWin = printFrame.contentWindow!;
  const frameDoc = frameWin.document;
  const attachmentsHTML = attachments?.map((attachment, index) => `
      ${attachment["body"]}
      <h3>${authorName} - ${formatDate(attachment["datelastmodified"])}</h3>
      `).join('');
      
      frameDoc.open();
      frameDoc.write(`
        <head><title>${title}</title></head>
        <body class="${printBackground ? "printBackground" : ""}">
          <br />
          <div class="break-line">${textToPrint ?? ""}</div>
          <h3 class="note-title">${noteDescription}</h3>
          ${attachmentsHTML ? attachmentsHTML : ""}
        </body>
    `);
    frameDoc.close();

  await new Promise<void>((resolve) => {
    const link = frameDoc.createElement("link");
    link.rel = "stylesheet";
    link.href = "/printText.css";

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };

    link.onload = () => finish();
    link.onerror = () => finish();
    frameDoc.head.appendChild(link);

    window.setTimeout(() => finish(), 8000);
  });

  frameWin.focus();
  frameWin.print();

  document.body.removeChild(printFrame);
};

export default printText;
